import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Fade from "@mui/material/Fade";
import Divider from "@mui/material/Divider";
import { alpha } from "@mui/material/styles";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import BoltIcon from "@mui/icons-material/Bolt";

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import ErrorBoundary from "components/ErrorBoundary";

// Services
import { RequestService } from "services/RequestService";

function AddTransaction({ powerStationId, onSuccess, onTransactionAdded, open, onClose }) {
  const [stUnits, setStUnits] = useState([]);
  const [gtUnits, setGtUnits] = useState([]);
  const [stStatuses, setStStatuses] = useState([]);
  const [powerStations, setPowerStations] = useState([]);
  const [selectedPowerStation, setSelectedPowerStation] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    frequency: "",
    target: "",
    expectedStValue: "",
    gasPressure: "",
    gasVolume: "",
    powerGenerator: "",
    stReadings: []
  });
  const [notification, setNotification] = useState({
    color: "info",
    icon: "notifications",
    title: "لوحة ادارة ربان السفينة",
    content: "",
    dateTime: "",
    open: false,
  });

  const closeNotification = () => setNotification({ ...notification, open: false });

  useEffect(() => {
    if (open) {
      fetchPowerStations();
      // Reset form when opening
      setFormData({
        frequency: "",
        target: "",
        expectedStValue: "",
        gasPressure: "",
        gasVolume: "",
        powerGenerator: "",
        stReadings: []
      });
      setErrors({});
      setSelectedPowerStation("");
    }
  }, [open]);

  useEffect(() => {
    if (selectedPowerStation) {
      fetchStUnits();
      fetchGtUnits();
      fetchGtStatuses();
    }
  }, [selectedPowerStation]);

  const fetchPowerStations = async () => {
    try {
      const response = await RequestService.get("PowerStation");
      if (response.data && !response.data.error) {
        setPowerStations(response.data.data || []);
      } else {
        setNotification({
          color: "error",
          icon: "warning",
          title: "Failed",
          content: response.data?.message || "Failed to load power stations",
          open: true
        });
      }
    } catch (error) {
      console.error("Error fetching power stations:", error);
      setNotification({
        color: "error",
        icon: "warning",
        title: "Failed",
        content: "Error loading power stations",
        open: true
      });
    }
  };

  useEffect(() => {
    if (stUnits.length > 0 && gtUnits.length > 0) {
      const initialStReadings = stUnits.map(st => ({
        stId: parseInt(st.id),
        stValue: "",
        expectedSTValue: "",
        gtReadings: gtUnits
          .filter(gt => gt.stId === st.id)
          .map(gt => ({
            gtId: parseInt(gt.id),
            gtValue: "",
            gtStatusId: 1
          }))
      }));
      
      setFormData(prev => ({
        ...prev,
        stReadings: initialStReadings,
        powerGenerator: "",
        target: "",
        gasPressure: "",
        gasVolume: "",
        frequency: ""
      }));
    }
  }, [stUnits, gtUnits]);

  const fetchStUnits = async () => {
    try {
      const response = await RequestService.get(`/ST/${selectedPowerStation}/sts`);
      if (response.data && !response.data.error) {
        setStUnits(response.data.data || []);
      } else {
        setNotification({
          color: "error",
          icon: "warning",
          title: "Failed",
          content: response.data?.message || "Failed to load ST units",
          open: true
        });
      }
    } catch (error) {
      console.error("Error fetching ST units:", error);
      setNotification({
        color: "error",
        icon: "warning",
        title: "Failed",
        content: "Error loading ST units",
        open: true
      });
    }
  };

  const fetchGtUnits = async () => {
    try {
      const response = await RequestService.get(`/GT/${selectedPowerStation}/gts`);
      if (response.data && !response.data.error) {
        setGtUnits(response.data.data || []);
      } else {
        setNotification({
          color: "error",
          icon: "warning",
          title: "فشلت العملية",
          content: response.data?.message || "فشل في تحميل الوحدات الغازية",
          open: true
        });
      }
    } catch (error) {
      console.error("Error fetching GT units:", error);
      setGtUnits([]);
      setNotification({
        color: "error",
        icon: "warning",
        title: "فشلت العملية",
        content: error.response?.data?.message || "حدث خطأ أثناء تحميل الوحدات الغازية",
        open: true
      });
    }
  };

  const fetchGtStatuses = async () => {
    try {
      const response = await RequestService.get("PowerStation/gt-statuses");
      if (response.data && !response.data.error) {
        setStStatuses(response.data.data || []);
      } else {
        setNotification({
          color: "error",
          icon: "warning",
          title: "Failed",
          content: response.data?.message || "Failed to load GT statuses",
          open: true
        });
      }
    } catch (error) {
      console.error("Error fetching GT statuses:", error);
      setNotification({
        color: "error",
        icon: "warning",
        title: "Failed",
        content: "Error loading GT statuses",
        open: true
      });
    }
  };

  const handleStValueChange = (stId, value) => {
    // Validate input is a number
    if (value && isNaN(value)) return;
    
    setFormData(prev => ({
      ...prev,
      stReadings: prev.stReadings.map(reading =>
        reading.stId === stId ? { ...reading, stValue: value } : reading
      )
    }));
    
    // Clear error when value changes
    if (errors[`stValue_${stId}`]) {
      setErrors(prev => ({ ...prev, [`stValue_${stId}`]: null }));
    }
  };

  const handleGtValueChange = (stId, gtId, value) => {
    // Validate input is a number
    if (value && isNaN(value)) return;
    
    setFormData(prev => ({
      ...prev,
      stReadings: prev.stReadings.map(reading =>
        reading.stId === stId
          ? {
              ...reading,
              gtReadings: reading.gtReadings.map(gt =>
                gt.gtId === gtId ? { ...gt, gtValue: value } : gt
              )
            }
          : reading
      )
    }));
    
    // Clear error when value changes
    if (errors[`gtValue_${gtId}`]) {
      setErrors(prev => ({ ...prev, [`gtValue_${gtId}`]: null }));
    }
  };

  const handleGtStatusChange = (stId, gtId, statusId) => {
    setFormData(prev => ({
      ...prev,
      stReadings: prev.stReadings.map(reading =>
        reading.stId === stId
          ? {
              ...reading,
              gtReadings: reading.gtReadings.map(gt =>
                gt.gtId === gtId ? { ...gt, gtStatusId: statusId } : gt
              )
            }
          : reading
      )
    }));
  };

  const handleExpectedStValueChange = (stId, value) => {
    // Validate input is a number
    if (value && isNaN(value)) return;
    
    setFormData(prev => ({
      ...prev,
      stReadings: prev.stReadings.map(reading =>
        reading.stId === stId ? { ...reading, expectedSTValue: value } : reading
      )
    }));
    
    // Clear error when value changes
    if (errors[`expectedSTValue_${stId}`]) {
      setErrors(prev => ({ ...prev, [`expectedSTValue_${stId}`]: null }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validate numeric fields
    if (['frequency', 'target', 'gasPressure', 'gasVolume', 'powerGenerator'].includes(name)) {
      if (value && isNaN(value)) return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Validate power station selection
    if (!selectedPowerStation) {
      newErrors.powerStationId = "يجب اختيار محطة الطاقة";
      isValid = false;
    }

    // Validate main fields
    if (!formData.frequency) {
      newErrors.frequency = "تردد الطاقة مطلوب";
      isValid = false;
    } else if (parseFloat(formData.frequency) < 45 || parseFloat(formData.frequency) > 65) {
      newErrors.frequency = "يجب أن يكون التردد بين 45 و 65";
      isValid = false;
    }

    if (!formData.target) {
      newErrors.target = "القيمة المستهدفة مطلوبة";
      isValid = false;
    } else if (parseFloat(formData.target) <= 0) {
      newErrors.target = "يجب أن تكون القيمة المستهدفة أكبر من الصفر";
      isValid = false;
    }

    if (!formData.gasPressure) {
      newErrors.gasPressure = "ضغط الغاز مطلوب";
      isValid = false;
    } else if (parseFloat(formData.gasPressure) <= 0) {
      newErrors.gasPressure = "يجب أن يكون ضغط الغاز أكبر من الصفر";
      isValid = false;
    }

    if (!formData.gasVolume) {
      newErrors.gasVolume = "حجم الغاز مطلوب";
      isValid = false;
    } else if (parseFloat(formData.gasVolume) <= 0) {
      newErrors.gasVolume = "يجب أن يكون حجم الغاز أكبر من الصفر";
      isValid = false;
    }

    if (!formData.powerGenerator) {
      newErrors.powerGenerator = "قوة المولد مطلوبة";
      isValid = false;
    } else if (parseFloat(formData.powerGenerator) <= 0) {
      newErrors.powerGenerator = "يجب أن تكون قوة المولد أكبر من الصفر";
      isValid = false;
    }

    // Validate ST and GT readings
    formData.stReadings.forEach(stReading => {
      if (!stReading.stValue) {
        newErrors[`stValue_${stReading.stId}`] = "قيمة التوربين البخاري مطلوبة";
        isValid = false;
      } else if (parseFloat(stReading.stValue) <= 0) {
        newErrors[`stValue_${stReading.stId}`] = "يجب أن تكون قيمة التوربين البخاري أكبر من الصفر";
        isValid = false;
      }

      if (!stReading.expectedSTValue) {
        newErrors[`expectedSTValue_${stReading.stId}`] = "القيمة المتوقعة للتوربين البخاري مطلوبة";
        isValid = false;
      } else if (parseFloat(stReading.expectedSTValue) <= 0) {
        newErrors[`expectedSTValue_${stReading.stId}`] = "يجب أن تكون القيمة المتوقعة أكبر من الصفر";
        isValid = false;
      }

      stReading.gtReadings.forEach(gtReading => {
        if (!gtReading.gtValue) {
          newErrors[`gtValue_${gtReading.gtId}`] = "قيمة التوربين الغازي مطلوبة";
          isValid = false;
        } else if (parseFloat(gtReading.gtValue) <= 0) {
          newErrors[`gtValue_${gtReading.gtId}`] = "يجب أن تكون قيمة التوربين الغازي أكبر من الصفر";
          isValid = false;
        }
      });
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Format the payload with numeric values
    const payload = {
      powerStationId: parseInt(selectedPowerStation),
      powerGenerator: parseFloat(formData.powerGenerator),
      target: parseFloat(formData.target),
      gasPressure: parseFloat(formData.gasPressure),
      gasVolume: parseFloat(formData.gasVolume),
      Frequency: parseFloat(formData.frequency),
      stReadings: formData.stReadings.map(stReading => ({
        stId: parseInt(stReading.stId),
        stValue: parseFloat(stReading.stValue),
        expectedSTValue: parseFloat(stReading.expectedSTValue),
        gtReadings: stReading.gtReadings.map(gtReading => ({
          gtId: parseInt(gtReading.gtId),
          gtValue: parseFloat(gtReading.gtValue),
          gtStatusId: gtReading.gtStatusId
        }))
      }))
    };

    setLoading(true);
    try {
      const response = await RequestService.post("transaction", payload);
      
      if (response.data && !response.data.error) {
        setNotification({
          color: "success",
          icon: "check",
          title: "تمت العملية بنجاح",
          content: response.data?.message || "تمت إضافة القراءة بنجاح",
          dateTime: new Date().toLocaleString('ar-IQ'),
          open: true
        });
        
        if (onSuccess) {
          onSuccess(response);
        }
        
        if (onTransactionAdded) {
          onTransactionAdded();
        }
        
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        throw new Error(response.data?.message || "فشل في إضافة القراءة");
      }
    } catch (error) {
      console.error("Error submitting transaction:", error);
      const errorData = error.response?.data;

      let content = "حدث خطأ أثناء إضافة القراءة";
      
      if (errorData?.errors) {
        // Handle validation errors from backend
        const backendErrors = {};
        
        // Convert backend errors to our format
        Object.entries(errorData.errors).forEach(([field, messages]) => {
          if (Array.isArray(messages)) {
            // Handle nested errors (for ST/GT readings)
            if (field.includes('stReadings')) {
              const match = field.match(/stReadions\[(\d+)\]\.(\w+)/);
              if (match) {
                const stIndex = match[1];
                const stField = match[2];
                const stId = formData.stReadings[stIndex]?.stId;
                if (stId) {
                  backendErrors[`${stField}_${stId}`] = messages.join(', ');
                }
              }
            } else if (field.includes('gtReadings')) {
              const match = field.match(/gtReadions\[(\d+)\]\.(\w+)/);
              if (match) {
                const gtIndex = match[1];
                const gtField = match[2];
                // Find the GT unit ID - this might need adjustment based on your data structure
                const gtId = gtUnits[gtIndex]?.id;
                if (gtId) {
                  backendErrors[`${gtField}_${gtId}`] = messages.join(', ');
                }
              }
            } else {
              // Handle top-level errors
              backendErrors[field] = messages.join(', ');
            }
          }
        });
        
        setErrors(prev => ({ ...prev, ...backendErrors }));
        content = "يوجد أخطاء في البيانات المدخلة. يرجى مراجعة الحقول المحددة.";
      } else if (errorData?.message) {
        content = errorData.message;
      }
      
      setNotification({
        color: "error",
        icon: "warning",
        title: "فشلت العملية",
        content,
        open: true
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ErrorBoundary>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            overflowY: 'auto',
            m: 2,
            borderRadius: 1,
          }
        }}
      >
        <DialogTitle sx={{ p: 3, pb: 1 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox display="flex" alignItems="center">
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="3.5rem"
                height="3.5rem"
                variant="gradient"
                bgColor="info"
                color="white"
                shadow="md"
                borderRadius="xl"
                mr={2}
              >
                <BoltIcon sx={{ fontSize: 28 }} />
              </MDBox>
              <MDBox>
                <MDTypography variant="h3" fontWeight="medium" color="info" mb={0.5}>
                  لوحة ادارة ربان السفينة
                </MDTypography>
                <MDTypography variant="body2" color="text" opacity={0.8}>
                  ادارة قراءات محطات الطاقة
                </MDTypography>
              </MDBox>
            </MDBox>
            <IconButton 
              onClick={onClose}
              size="large"
              sx={{
                color: 'text.secondary',
                transition: 'all 0.2s',
                '&:hover': {
                  color: 'error.main',
                  transform: 'rotate(90deg)',
                },
              }}
            >
              <CloseIcon fontSize="large" />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 2 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Power Station Selection */}
              <Grid item xs={12}>
                <FormControl 
                  fullWidth 
                  error={!!errors.powerStationId}
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      height: '45px'
                    }
                  }}
                >
                  <InputLabel>محطة الطاقة</InputLabel>
                  <Select
                    value={selectedPowerStation}
                    onChange={(e) => setSelectedPowerStation(e.target.value)}
                    required
                    MenuProps={{
                      PaperProps: {
                        sx: { maxHeight: 200 }
                      }
                    }}
                  >
                    {powerStations.map((station) => (
                      <MenuItem key={station.id} value={station.id}>
                        {station.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.powerStationId && (
                    <FormHelperText>{errors.powerStationId}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              {/* Measurements Section */}
              <Grid item xs={12}>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}>
                    <MDInput
                      type="number"
                      label="التردد"
                      name="frequency"
                      value={formData.frequency}
                      onChange={handleInputChange}
                      error={!!errors.frequency}
                      helperText={errors.frequency}
                      inputProps={{
                        step: "0.01",
                        min: "45",
                        max: "65"
                      }}
                      required
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <MDInput
                      type="number"
                      label="القيمة المستهدفة"
                      name="target"
                      value={formData.target}
                      onChange={handleInputChange}
                      error={!!errors.target}
                      helperText={errors.target}
                      inputProps={{
                        step: "0.01",
                        min: "0.01"
                      }}
                      required
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Grid>
              
              {/* Gas and Power Section */}
              <Grid item xs={12}>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={4}>
                    <MDBox mb={2}>
                      <MDInput
                        type="number"
                        label="ضغط الغاز"
                        name="gasPressure"
                        value={formData.gasPressure}
                        onChange={handleInputChange}
                        error={!!errors.gasPressure}
                        helperText={errors.gasPressure}
                        inputProps={{
                          step: "0.01",
                          min: "0.01"
                        }}
                        required
                        fullWidth
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <MDBox mb={2}>
                      <MDInput
                        type="number"
                        label="تدفق الغاز (م³/ساعة)"
                        name="gasVolume"
                        value={formData.gasVolume}
                        onChange={handleInputChange}
                        error={!!errors.gasVolume}
                        helperText={errors.gasVolume}
                        inputProps={{
                          step: "0.01",
                          min: "0.01"
                        }}
                        required
                        fullWidth
                      />
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <MDBox mb={2}>
                      <MDInput
                        type="number"
                        label="قوة المولد"
                        name="powerGenerator"
                        value={formData.powerGenerator}
                        onChange={handleInputChange}
                        error={!!errors.powerGenerator}
                        helperText={errors.powerGenerator}
                        inputProps={{
                          step: "0.01",
                          min: "0.01"
                        }}
                        required
                        fullWidth
                      />
                    </MDBox>
                  </Grid>
                </Grid>
              </Grid>

              {selectedPowerStation && formData.stReadings.map((stReading, stIndex) => {
                const st = stUnits.find(unit => unit.id === stReading.stId);
                return (
                  <Grid item xs={12} key={stReading.stId}>
                    <MDBox mb={3} sx={{ 
                      p: 2, 
                      border: '1px solid',
                      borderColor: 'info.main',
                      borderRadius: 1,
                      '&:hover': {
                        boxShadow: '0 0 10px rgba(26, 115, 232, 0.1)'
                      }
                    }}>
                      <MDTypography variant="h6" mb={2} color="info.main">
                        {st?.name || `توربين بخاري ${stIndex + 1}`}
                      </MDTypography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <MDInput
                            type="number"
                            label="قيمة التوربين البخاري"
                            value={stReading.stValue}
                            onChange={(e) => handleStValueChange(stReading.stId, e.target.value)}
                            error={!!errors[`stValue_${stReading.stId}`]}
                            helperText={errors[`stValue_${stReading.stId}`]}
                            inputProps={{
                              step: "0.01",
                              min: "0.01"
                            }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <MDInput
                            type="number"
                            label="القيمة المتوقعة للتوربين البخاري"
                            value={stReading.expectedSTValue}
                            onChange={(e) => handleExpectedStValueChange(stReading.stId, e.target.value)}
                            error={!!errors[`expectedSTValue_${stReading.stId}`]}
                            helperText={errors[`expectedSTValue_${stReading.stId}`]}
                            inputProps={{
                              step: "0.01",
                              min: "0.01"
                            }}
                            fullWidth
                          />
                        </Grid>
                      </Grid>

                      <MDBox mt={2}>
                        <MDTypography variant="subtitle2" mb={1} color="info">
                          التوربينات الغازية:
                        </MDTypography>
                        <Grid container spacing={2}>
                          {stReading.gtReadings.map((gtReading) => {
                            const gt = gtUnits.find(unit => unit.id === gtReading.gtId);
                            return (
                              <Grid item xs={12} key={gtReading.gtId}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <MDInput
                                      type="number"
                                      label={gt?.name || "قيمة التوربين الغازي"}
                                      value={gtReading.gtValue}
                                      onChange={(e) => handleGtValueChange(stReading.stId, gtReading.gtId, e.target.value)}
                                      error={!!errors[`gtValue_${gtReading.gtId}`]}
                                      helperText={errors[`gtValue_${gtReading.gtId}`]}
                                      inputProps={{
                                        step: "0.01",
                                        min: "0.01"
                                      }}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          height: '45px'
                                        }
                                      }}
                                      fullWidth
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                      <InputLabel>حالة التوربين الغازي</InputLabel>
                                      <Select
                                        value={gtReading.gtStatusId}
                                        onChange={(e) => handleGtStatusChange(stReading.stId, gtReading.gtId, e.target.value)}
                                        label="حالة التوربين الغازي"
                                        error={!!errors[`gtStatus_${gtReading.gtId}`]}
                                        style={{ height: '45px' }}
                                        MenuProps={{
                                          PaperProps: {
                                            sx: { maxHeight: 200 }
                                          }
                                        }}
                                      >
                                        {stStatuses.map((status) => (
                                          <MenuItem key={status.id} value={status.id}>
                                            {status.name}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                </Grid>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </MDBox>
                    </MDBox>
                  </Grid>
                );
              })}
            </Grid>
          </form>
        </DialogContent>
        <Divider />
        <DialogActions>
          <MDBox 
            display="flex" 
            gap={2} 
            width="100%" 
            justifyContent="space-between" 
            p={3}
            sx={{ maxWidth: 1400, margin: '0 auto' }}
          >
            <MDButton
              onClick={handleSubmit}
              variant="gradient"
              color="info"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? "جاري الإضافة..." : "إضافة"}
            </MDButton>
            <MDButton
              onClick={onClose}
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
            >
              إلغاء
            </MDButton>
          </MDBox>
        </DialogActions>
      </Dialog>
      <MDSnackbar
        color={notification.color} 
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        dateTime={notification.dateTime}
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
      />
    </ErrorBoundary>
  );
}

AddTransaction.propTypes = {
  powerStationId: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
  onTransactionAdded: PropTypes.func,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AddTransaction;